import { type HeaderSwitcherProps } from "@kazaar-platform/design-system";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { type WorkspaceEntity } from "@/api/__generated__/orval/accounts";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission";
import { partnershipWorkspacesAtom, workspacesAtom } from "@/store";
import { currentWorkspaceIdAtom } from "@/store/atoms/currentWorkspaceId";

type HeaderTree = HeaderSwitcherProps["tree"];

export type UseHeaderTreeResult = {
  headerTree: HeaderTree;
  selectedWorkspaceValue: number | undefined;
  onWorkspaceSelect: (value: number) => void;
};

/**
 * Builds a nested tree structure for the HeaderSwitcher component.
 * Workspaces are grouped by organization
 * Internally maintains bidirectional maps between numeric values (used by HeaderSwitcher) and workspace IDs.
 */
const buildTree = (
  workspaces: WorkspaceEntity[],
  groupLabel: (ws: WorkspaceEntity) => string
): {
  headerTree: HeaderTree;
  valueToWorkspaceId: Map<number, string>;
  workspaceIdToValue: Map<string, number>;
} => {
  const valueToWorkspaceId = new Map<number, string>();
  const workspaceIdToValue = new Map<string, number>();

  const byOrg = workspaces.reduce<
    Record<string, { label: string; workspaces: WorkspaceEntity[] }>
  >((acc, ws) => {
    if (!acc[ws.organizationId]) {
      acc[ws.organizationId] = { label: groupLabel(ws), workspaces: [] };
    }
    acc[ws.organizationId].workspaces.push(ws);
    return acc;
  }, {});

  let counter = 0;

  const headerTree = Object.entries(byOrg).map(
    ([, { label, workspaces: orgWs }], id) => ({
      id,
      label,
      tree: orgWs.map((ws) => {
        const value = counter++;
        valueToWorkspaceId.set(value, ws.id);
        workspaceIdToValue.set(ws.id, value);
        return { label: ws.name, value };
      }),
    })
  );

  return { headerTree, valueToWorkspaceId, workspaceIdToValue };
};

export const useHeaderTree = (): UseHeaderTreeResult => {
  const { hasBrokerKazaarRole, hasAdvertiserKazaarRole } = useRolePermission();
  const workspaces = useAtomValue(workspacesAtom);

  const advertiserWorkspaces = useAtomValue(workspacesAtom);
  const currentWorkspaceId = useAtomValue(currentWorkspaceIdAtom);
  const setCurrentWorkspaceId = useSetAtom(currentWorkspaceIdAtom);
  const setPartnershipWorkspaces = useSetAtom(partnershipWorkspacesAtom);

  // Sync the fetched workspaces into the partnershipWorkspacesAtom so that currentWorkspaceAtom can resolve the selected workspace entity.
  useEffect(() => {
    if (hasBrokerKazaarRole) {
      setPartnershipWorkspaces(workspaces);
    } else {
      setPartnershipWorkspaces([]);
    }
  }, [hasBrokerKazaarRole, setPartnershipWorkspaces, workspaces]);

  // Build the tree structure for the HeaderSwitcher component.
  const { headerTree, valueToWorkspaceId, workspaceIdToValue } = useMemo(() => {
    if (hasBrokerKazaarRole) {
      return buildTree(
        workspaces,
        (ws) => `${ws.accountName} | ${ws.organizationName}`
      );
    }

    if (hasAdvertiserKazaarRole) {
      return buildTree(advertiserWorkspaces, (ws) => ws.organizationName);
    }

    return {
      headerTree: [] as HeaderTree,
      valueToWorkspaceId: new Map<number, string>(),
      workspaceIdToValue: new Map<string, number>(),
    };
  }, [
    hasBrokerKazaarRole,
    hasAdvertiserKazaarRole,
    workspaces,
    advertiserWorkspaces,
  ]);

  const onWorkspaceSelect = (value: number) => {
    const wsId = valueToWorkspaceId.get(value);

    if (wsId) {
      setCurrentWorkspaceId(wsId);
    }
  };

  return {
    headerTree,
    selectedWorkspaceValue: workspaceIdToValue.get(currentWorkspaceId),
    onWorkspaceSelect,
  };
};
